import { useEffect, useRef, useState } from 'react';
import { cooperplaceApi } from 'services/api';
import { validateOccurrences } from './contract';

// 50 por página no servidor: 20 páginas cobrem 1.000 ocorrências numa viagem.
const MAX_PAGES = 20;

/**
 * Lê todas as páginas de ocorrências da viagem (5660): as abas Pendentes e Concluídas e os totais
 * das etiquetas valem para a viagem inteira, não só para a primeira página.
 */
export default function useOccurrences(companyId, travelId, revision) {
  const companyScope = companyId == null || companyId === '' ? 'session' : String(companyId);
  const key = `${companyScope}:${travelId}:${revision}`;
  const generation = useRef(0);
  const [state, setState] = useState({ key: null, loading: true, data: null, error: null });
  useEffect(() => {
    const current = ++generation.current;
    const abort = new AbortController();
    setState({ key, loading: true, data: null, error: null });
    async function read() {
      try {
        if (!Number.isSafeInteger(travelId) || travelId < 1)
          throw new Error('Contexto da viagem não informado.');
        const pages = [];
        for (let page = 1; ; page += 1) {
          if (page > MAX_PAGES)
            throw new Error('Viagem com ocorrências demais para esta tela. Consulte pela viagem.');
          const reply = await cooperplaceApi.get(`control-tower/travels/${travelId}/occurrences`, {
            params: { page },
            signal: abort.signal,
          });
          const data = validateOccurrences(reply.data, travelId, page);
          const first = pages[0]?.summary;
          if (
            first &&
            (first.totalCount !== data.summary.totalCount ||
              first.pendingCount !== data.summary.pendingCount)
          )
            throw new Error('As ocorrências mudaram durante a consulta. Tente novamente.');
          pages.push(data);
          if (!data.pagination.hasMore) break;
        }
        const rows = pages.flatMap((page) => page.data);
        if (new Set(rows.map((row) => row.id)).size !== rows.length)
          throw new Error('As ocorrências mudaram durante a consulta. Tente novamente.');
        if (current === generation.current && !abort.signal.aborted)
          setState({ key, loading: false, data: { ...pages[0], data: rows }, error: null });
      } catch (error) {
        if (current !== generation.current || abort.signal.aborted) return;
        const status = error.response?.status;
        const message = [401, 403].includes(status)
          ? 'Você não tem permissão para consultar as ocorrências.'
          : status === 404
            ? 'Viagem não encontrada ou indisponível para consulta.'
            : error.response || error.isAxiosError
              ? 'Não foi possível consultar as ocorrências. Tente novamente.'
              : error.message;
        setState({ key, loading: false, data: null, error: message });
      }
    }
    read();
    return () => abort.abort();
  }, [key, travelId]);
  return state.key === key ? state : { loading: true, data: null, error: null };
}
